/** @format */
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { CustomStudyDraft, OtmId, UniversitySettings, Workflow } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { combineLatest, map, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppErrorHandler, OrganisationNameService, UniversityService, WorkflowEntityService } from 'sis-components';

export interface StudentCustomStudyDraftOptions {
    priorLearningInclusionApplicationHidden?: boolean;
    customAttainmentApplicationHidden?: boolean;
    internshipCustomAttainmentApplicationHidden?: boolean;
    customAttainmentApplicationsDisabled?: boolean;
}

export interface StudentCustomStudyDraftDialogValues {
    options: StudentCustomStudyDraftOptions;
    studentId: OtmId;
    customStudyDraft: CustomStudyDraft;
}

interface TemplateData {
    studyDraft: CustomStudyDraft;
    viewOptions: StudentCustomStudyDraftOptions;
    homeUniversityName: string;
    isNewApplicationAllowed: boolean;
    isSuggestButtonVisible: boolean;
}

@Component({
    selector: 'app-student-custom-study-draft-dialog',
    templateUrl: './student-custom-study-draft-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class StudentCustomStudyDraftDialogComponent implements OnInit {
    suggestAttainment: boolean;
    templateData$: Observable<TemplateData>;

    constructor(
        public dialogService: FudisDialogService,
        private readonly universityService: UniversityService,
        private readonly organisationNameService: OrganisationNameService,
        private readonly appErrorHandler: AppErrorHandler,
        private readonly workflowService: WorkflowEntityService,
        @Inject(MAT_DIALOG_DATA) readonly data: StudentCustomStudyDraftDialogValues,
    ) {}

    ngOnInit(): void {
        this.suggestAttainment = false;

        if (this.data?.customStudyDraft && this.data?.studentId) {
            const universitySettings$ = this.createUniversitySettingsObservable();
            const workflows$ = this.createStudentWorkflowObservable(this.data.studentId, this.data.customStudyDraft.id);
            this.templateData$ = this.createDataObservable(universitySettings$, workflows$);
        }
    }

    createDataObservable(
        universitySettings$: Observable<UniversitySettings>,
        workflows$: Observable<Workflow[]>,
    ): Observable<TemplateData> {
        const homeUniversityName$ = this.organisationNameService.getOrgName(this.universityService.getCurrentUniversityOrgId());
        return combineLatest([universitySettings$, workflows$, homeUniversityName$]).pipe(
            map(([universitySettings, workflows, homeUniversityName]) => {
                const studyDraft = this.data.customStudyDraft;
                const viewOptions = this.resolveCustomStudyDraftOptions(this.data.options, universitySettings);
                const isNewApplicationAllowed = this.isNewApplicationAllowed(workflows, viewOptions.customAttainmentApplicationsDisabled);
                const isSuggestButtonVisible = this.isSuggestButtonVisible(viewOptions, isNewApplicationAllowed);
                return { studyDraft, viewOptions, homeUniversityName, isNewApplicationAllowed, isSuggestButtonVisible };
            }),
        );
    }

    createUniversitySettingsObservable(): Observable<UniversitySettings> {
        return this.universityService.getCurrentUniversitySettings().pipe(take(1), this.appErrorHandler.defaultErrorHandler());
    }

    createStudentWorkflowObservable(studentId: string, customStudyDraftId: string): Observable<Workflow[]> {
        return this.workflowService.getStudentWorkflowsMatchingCustomStudyDraft(studentId, customStudyDraftId).pipe(
            take(1),
            map(workflows =>
                workflows.filter(workflow => _.includes(['REQUESTED', 'ACCEPTED', 'IN_HANDLING', 'CONDITIONAL'], workflow.state)),
            ),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    cancel(): void {
        this.dialogService.close();
    }

    startAttainmentApplicationProcess(workflowOperationType: string): void {
        this.dialogService.close(workflowOperationType);
    }

    isNewApplicationAllowed(workflows: Workflow[], customAttainmentApplicationsDisabled: boolean): boolean {
        return workflows.length < 1 && !customAttainmentApplicationsDisabled;
    }

    isSuggestButtonVisible(options: StudentCustomStudyDraftOptions, isNewApplicationAllowed: boolean): boolean {
        if (
            options.priorLearningInclusionApplicationHidden &&
            options.customAttainmentApplicationHidden &&
            options.internshipCustomAttainmentApplicationHidden
        ) {
            return false;
        }
        return isNewApplicationAllowed;
    }

    resolveCustomStudyDraftOptions(
        options: StudentCustomStudyDraftOptions,
        universitySettings: UniversitySettings,
    ): StudentCustomStudyDraftOptions {
        return {
            priorLearningInclusionApplicationHidden: _.defaultTo(
                options.priorLearningInclusionApplicationHidden,
                !(universitySettings?.frontendFeatureToggles?.priorLearningInclusionApplicationEnabled ?? true),
            ),
            customAttainmentApplicationHidden: _.defaultTo(
                options.customAttainmentApplicationHidden,
                !(universitySettings?.frontendFeatureToggles?.customCourseCreditApplicationEnabled ?? true),
            ),
            internshipCustomAttainmentApplicationHidden: _.defaultTo(
                options.internshipCustomAttainmentApplicationHidden,
                !universitySettings?.frontendFeatureToggles?.internshipCustomAttainmentApplicationEnabled,
            ),
            customAttainmentApplicationsDisabled: _.defaultTo(options.customAttainmentApplicationsDisabled, true),
        };
    }
}
